import {tryToInlineScript} from './tryToInlineScript';
import {attachOnErrorListenerToScript} from './attachOnErrorListenerToScript';

/**
 * When provided with an HTML string this function:
 * - does nothing if there are no script tags with `src` attribute in HTML
 *
 * - ensures that all scripts in the HTML are executed in the order they appear in the HTML with respecting `async`, `defer` and `type="module"` attributes. This will allow us to execute scripts in the same way they would be executed on the published sites.
 * This is done because dynamically added scripts are `async` by default (https://tr.javascript.info/script-async-defer#dynamic-scripts) but we need them to behave synchronously.
 * Solution used here is inspired by https://stackoverflow.com/questions/2879509/dynamically-loading-javascript-synchronously/2880147#2880147
 *
 * - adds error listener to all not inline scripts that will be executed when the script fails to load. We are mostly expecting scripts to fail loading due to CORS issues.
 */
export const updateScriptTagsInHtml = (html: string) => {
  // Early return if there are no script tags
  if (!html.includes('<script') || html === '') {
    return html;
  }

  const parser = new DOMParser();
  let doc: Document;

  try {
    doc = parser.parseFromString(html, 'text/html');
  } catch (error) {
    return html; // Return original HTML if parsing fails
  }

  // Check if parsing was successful
  if (doc.querySelector('parsererror')) {
    return html; // Return original HTML if parsing fails
  }

  let updatedHtml = html;

  // Get all script tags in the parsed HTML
  const scripts = doc.querySelectorAll('script');

  scripts.forEach((scriptTag) => {
    if (shouldScriptWithSrcBeExecutedImmediately(scriptTag)) {
      updatedHtml = tryToInlineScript({
        scriptTag,
        doc,
        updatedHtml,
      });
      // Attach an error listener to all not inline scripts
    } else if (scriptTag.src) {
      updatedHtml = attachOnErrorListenerToScript({
        html: updatedHtml,
        scriptTag,
        doc,
      });
    }
  });

  return updatedHtml;
};

/**
 * Checks if the script is external (i.e., has a 'src' attribute) and should be immediately executed (= is not async/defer/module)
 */
const shouldScriptWithSrcBeExecutedImmediately = (
  scriptTag: HTMLScriptElement
) => {
  return (
    scriptTag.src &&
    !scriptTag.hasAttribute('async') &&
    !scriptTag.hasAttribute('defer') &&
    scriptTag.type !== 'module'
  );
};
